import type { IDashboardPeriodWorkspaceGroup, IDashboardSearch } from '@/interfaces/stores/dashboard'

export const DASHBOARD_SEARCH_COLOR_PALETTE = [
  '#123ADD',
  '#EAB308',
  '#099657',
  '#8b5cf6',
  '#FB5454',
  '#042194',
  '#0eca8b',
  '#D97706',
  '#7C3AED',
  '#0891B2',
  '#DB2777',
] as const

function normalizeDashboardColorHex(c: string): string {
  return c.trim().toLowerCase()
}

export function pickNextDashboardSearchColor(existingHexColors: readonly string[]): string {
  const used = new Set(existingHexColors.map(normalizeDashboardColorHex))
  for (const hex of DASHBOARD_SEARCH_COLOR_PALETTE) {
    const key = normalizeDashboardColorHex(hex)
    if (!used.has(key)) return hex
  }
  const n = used.size
  const hue = (41 + n * 47) % 360
  return `hsl(${hue} 72% 42%)`
}

export function dedupeDashboardSearchColors(searches: IDashboardSearch[]): IDashboardSearch[] {
  const used = new Set<string>()
  return searches.map((s) => {
    const key = normalizeDashboardColorHex(s.color)
    if (!used.has(key)) {
      used.add(key)
      return s
    }
    const next = pickNextDashboardSearchColor(Array.from(used))
    used.add(normalizeDashboardColorHex(next))
    return { ...s, color: next }
  })
}

export function buildDashboardSearchCacheKey(
  mode: 'sensor' | 'group',
  entityId: number,
  dateRangeQuery: string,
  shiftId: number | null,
): string {
  return `${mode}:${entityId}:${dateRangeQuery}:shift:${shiftId ?? 'all'}`
}

export function getDashboardPeriodKey(search: Pick<IDashboardSearch, 'dateRangeQuery' | 'shiftId'>): string {
  return `${search.dateRangeQuery}|shift:${search.shiftId ?? 'all'}`
}

export function normalizeDashboardPeriod(period: unknown): Date[] {
  if (!Array.isArray(period)) return []
  return period.map((d) => (d instanceof Date ? d : new Date(String(d))))
}

export function formatDashboardPeriodRangeLabel(period: unknown): string {
  const [a, b] = normalizeDashboardPeriod(period)
  if (!a || !b) return ''
  const opts: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  return `${a.toLocaleString('pt-BR', opts)} — ${b.toLocaleString('pt-BR', opts)}`
}

export function groupDashboardVisibleSearchesByPeriod(
  orderedVisible: IDashboardSearch[],
): IDashboardPeriodWorkspaceGroup[] {
  const keyOrder: string[] = []
  const byKey = new Map<string, IDashboardSearch[]>()
  for (const s of orderedVisible) {
    const k = getDashboardPeriodKey(s)
    if (!byKey.has(k)) {
      byKey.set(k, [])
      keyOrder.push(k)
    }
    byKey.get(k)!.push(s)
  }
  return keyOrder.map((periodKey) => ({ periodKey, searches: byKey.get(periodKey)! }))
}

export function getDashboardSideBySideGridClass(visibleCount: number): string {
  const base =
    'grid w-full min-w-0 max-w-full items-start gap-6 [&>*]:min-w-0 [&>*]:w-full [&>*]:max-w-full [&>*]:self-start'
  if (visibleCount <= 1) {
    return `${base} grid-cols-1`
  }
  if (visibleCount === 2) {
    return `${base} grid-cols-1 md:[grid-template-columns:repeat(2,minmax(0,1fr))]`
  }
  if (visibleCount === 3) {
    return `${base} grid-cols-1 md:[grid-template-columns:repeat(2,minmax(0,1fr))] xl:[grid-template-columns:repeat(3,minmax(0,1fr))]`
  }
  return `${base} grid-cols-1 md:[grid-template-columns:repeat(2,minmax(0,1fr))] xl:[grid-template-columns:repeat(3,minmax(0,1fr))] 2xl:[grid-template-columns:repeat(4,minmax(0,1fr))]`
}

export function getDashboardPeriodGroupsColumnWeights(blockCountsPerGroup: number[]): number[] {
  return blockCountsPerGroup.map((raw) => {
    const c = Math.max(1, raw)
    if (c === 1) return 1.12
    return c + 0.48
  })
}
